import React from "react";
import { MathJax } from "better-react-mathjax";

export function InterpolationIntro({ lite = false }) {
  const baseClass = lite
    ? 'space-y-2 text-justify hyphens-auto text-sm leading-relaxed text-white/80 md:text-base'
    : 'space-y-4 text-justify hyphens-auto leading-relaxed text-white/85 text-sm md:text-base';
  return (
    <div className={baseClass}>
      Interpolation reconstructs an unknown function from sampled data points. Given
      nodes <MathJax inline>{"\\( x_0, \\dots, x_n \\)"}</MathJax> and associated
      values <MathJax inline>{"\\( y_i \\approx f(x_i) \\)"}</MathJax>, the goal is to build a
      surrogate function <MathJax inline>{"\\( p(x) \\)"}</MathJax> that matches the
      samples, so that <MathJax inline>{"\\( p(x_i) = y_i \\)"}</MathJax>.
      Depending on context, this interpolant might be a single polynomial of degree <MathJax inline>{"\\( n \\)"}</MathJax>, or a
      piecewise construction such as splines that guarantee continuity and smoothness.

      Beyond simply fitting the data, interpolation provides a practical way to estimate values between samples.
      It is widely used in computer graphics for smooth rendering, in signal processing for resampling and reconstruction,
      in geometric modeling for designing curves and surfaces, and in scientific computing for approximating functions defined
      only at discrete points. In data analytics, interpolation can fill gaps, smooth trends, or preserve shapes in tabulated datasets.
      Its importance lies in combining accuracy at known nodes with desirable properties such as efficiency, smoothness, and stability between them.
    </div>
  );
}

export default function InterpolationLongExplanation() {
  return (
    <div className="space-y-6 text-sm leading-relaxed text-white/80 text-justify hyphens-auto md:text-base">
      <h2 className="text-sm font-semibold md:text-base">1. Problem Setup & Motivation</h2>
      <p>
        Given distinct nodes <MathJax inline>{"\\( a\\le x_0<\\cdots<x_n\\le b \\)"}</MathJax> and values{" "}
        <MathJax inline>{"\\( y_i=f(x_i) \\)"}</MathJax>, we seek an interpolant{" "}
        <MathJax inline>{"\\( \\,\\mathcal{I}[\\{(x_i,y_i)\\}]\\, \\)"}</MathJax> that is cheap to evaluate and
        captures the qualitative behavior of <MathJax inline>{"\\( f \\)"}</MathJax>.
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Graphics/geometry:</strong> curves/surfaces through control points.</li>
        <li><strong>Scientific computing:</strong> tabulated functions, PDE post-processing.</li>
        <li><strong>Data science:</strong> fast surrogates for expensive simulators.</li>
        <li><strong>Signal processing:</strong> resampling, anti-aliasing filters.</li>
      </ul>

      <h2 className="text-sm font-semibold mt-4 md:text-base">2. Polynomial Interpolation: Existence & Uniqueness</h2>
      <p>


        For a set of distinct interpolation nodes <MathJax inline>{"\\( x_0, x_1,\\dots, x_n \\)"}</MathJax>, there exists a unique
        polynomial <MathJax inline>{"\\( p\\in\\Pi_n \\)"}</MathJax> of degree at most <MathJax inline>{"n "}</MathJax>
        such that <MathJax inline>{"\\( p(x_i) = y_i \\)"}</MathJax> for all <MathJax inline>{"\\( i \\)"}</MathJax>. This statement is
        the cornerstone of polynomial interpolation theory. One standard proof proceeds by expressing the polynomial in the monomial basis,
        <MathJax>{String.raw`\[
          p(x) = \sum_{j=0}^n c_j x^j,
        \]`}</MathJax>
        and imposing the interpolation conditions, which leads to the Vandermonde system
        <MathJax>{String.raw`\[
        Vc = y, \quad V_{ij} = x_i^{\,j}.
        \]`}</MathJax>
        The determinant of this matrix is given by
        <MathJax>{String.raw`\[
        \det V = \prod_{i<j}(x_j - x_i), 
        \]`}</MathJax>
        which is nonzero precisely because the nodes are distinct. Consequently, the system admits a unique solution
        for the coefficient vector <MathJax inline>{"\\( c \\)"}</MathJax>, guaranteeing the existence and uniqueness of the interpolating polynomial.

        Beyond establishing this theoretical result, the Vandermonde formulation also illustrates the algebraic structure underlying
        interpolation. However, in numerical practice the Vandermonde system is often extremely ill-conditioned for large <MathJax inline>{"\\( n \\)"}</MathJax>,
        which motivates the use of alternative constructive formulas such as the Lagrange form or the Newton form. These equivalent
        representations preserve the uniqueness of the interpolant while offering improved stability and efficiency for computation.
        Thus, the uniqueness theorem is not only of theoretical importance, but also serves as a foundation for the practical design and
        implementation of interpolation algorithms in scientific computing.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">Lagrange Form</h2>
      <p>
        The Lagrange basis polynomials provide an explicit and constructive way to define the interpolant.
        For given distinct nodes <MathJax inline>{"\\( x_0, \\dots, x_n \\)"}</MathJax>, each basis polynomial is given by
      </p>
      <MathJax>{String.raw`\[
  \ell_i(x)=\prod_{\substack{j=0\\ j\ne i}}^{n} \frac{x-x_j}{x_i-x_j}, \qquad
  \ell_i(x_j)=\delta_{ij}.
\]`}</MathJax>
      <p>
        These functions satisfy the property that <MathJax inline>{"\\( \\ell_i(x_j)=1 \\)"} </MathJax> if
        <MathJax inline>{"\\( i=j \\)"}</MathJax> and zero otherwise. As a result, the interpolating polynomial takes the form
      </p>
      <MathJax>{String.raw`\[
  p(x)=\sum_{i=0}^{n} y_i\,\ell_i(x).
\]`}</MathJax>
      <p>
        This representation makes the interpolation property completely transparent: each data value
        <MathJax inline>{"\\( y_i \\)"}</MathJax> contributes exactly at its node. The advantage of the Lagrange form lies
        in its simplicity and its explicit construction. However, direct evaluation requires
        <em> O</em>(<MathJax inline>{"\\( n^2 \\)"}</MathJax>) operations and is prone to rounding errors for large <MathJax inline>{"\\( n \\)"}</MathJax>.
        For practical computation, alternative forms or barycentric evaluation
        are typically preferred.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">Newton Form & Divided Differences</h2>
      <p>
        An alternative representation is the Newton form, which builds the interpolant incrementally.
        The Newton basis is defined as <MathJax inline>{"\\( \\omega_k(x)=\\prod_{j=0}^{k-1}(x-x_j) \\)"}</MathJax>, leading to
      </p>
      <MathJax>{String.raw`\[
      \begin{aligned}
  p(x)=a_0 +& a_1(x-x_0) + a_2(x-x_0)(x-x_1)\\
  +&\cdots+a_n\prod_{j=0}^{n-1}(x-x_j).
  \end{aligned}
\]`}</MathJax>
      <p>
        The coefficients <MathJax inline>{"\\( a_k \\)"}</MathJax> are determined via the recursive 
        scheme of <em>divided differences</em>:
      </p>
      <MathJax>{String.raw`\[
      \begin{aligned}
  f[x_i]&=y_i,\\
  f[x_i,x_{i+1}] &= \frac{f[x_{i+1}]-f[x_i]}{x_{i+1}-x_i},\\
  f[x_i,\ldots,x_{i+k}]
  =& \frac{1}{x_{i+k}-x_i}\big(f[x_{i+1},\ldots,x_{i+k}] \\
  &- f[x_i,\ldots,x_{i+k-1}]\big).
  \end{aligned}
\]`}</MathJax>
      <p>
        Then <MathJax inline>{"\\( a_k=f[x_0,\\dots,x_k] \\)"}</MathJax>. This form requires (<MathJax inline>{"O\\( n^2 \\)"}</MathJax>) preprocessing,
        but allows  <em>O</em>(<MathJax inline>{"\\( n \\)"}</MathJax>) evaluation using nested Horner’s scheme.
        A key practical advantage is that when a new interpolation point is added, only the last column of
        the divided-difference table must be updated, making this form particularly convenient for dynamic
        data sets.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">5. Error Formula & Conditioning</h2>
      <p>
        A central theoretical result is the interpolation error formula. If
        <MathJax inline>{"\\( f\\in C^{n+1}[a,b] \\)"}</MathJax>, then for each <MathJax inline>{"\\( x\\in[a,b] \\)"}</MathJax>
        there exists some intermediate point <MathJax inline>{"\\( \\xi(x) \\in [a,b] \\)"}</MathJax> such that
      </p>
      <MathJax>{String.raw`\[
      \begin{aligned}
  f(x)-p(x)&=\frac{f^{(n+1)}(\xi(x))}{(n+1)!}\; \omega(x),\\
  \qquad \omega(x)&:=\prod_{i=0}^n (x-x_i).
  \end{aligned}
\]`}</MathJax>
      <p>
        This formula shows that the error depends both on the smoothness of the function
        (via its <MathJax inline>{"\\( (n+1) \\)"} </MathJax>-st derivative) and the distribution of nodes
        (via <MathJax inline>{"\\( \\omega(x) \\)"}</MathJax>). In theory, if
        <MathJax inline>{"\\( f^{(n+1)} \\)"}</MathJax> is small, the error decreases rapidly with increasing
        <MathJax inline>{"\\( n \\)"}</MathJax>. In practice, however, the <em>Vandermonde system</em> is severely
        ill-conditioned, especially for equidistant nodes, leading to phenomena such as the
        <em>Runge phenomenon</em>. For numerical stability, barycentric or Newton forms, together with
        carefully chosen nodes, are strongly preferred.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">6. Barycentric Interpolation (Stable Evaluation)</h2>
      <p>
        The barycentric form provides a highly efficient and numerically stable method for evaluating the
        interpolant. One first precomputes the barycentric weights
      </p>
      <MathJax>{String.raw`\[
  w_i=\frac{1}{\displaystyle\prod_{\substack{j=0\\ j\ne i}}^{n} (x_i-x_j)}.
\]`}</MathJax>
      <p>
        Then, for any evaluation point <MathJax inline>{"\\( x\\ne x_i \\)"}</MathJax>, the interpolant can be computed as
      </p>
      <MathJax>{String.raw`\[
  p(x)= \frac{\displaystyle\sum_{i=0}^n \frac{w_i\,y_i}{x-x_i}}
              {\displaystyle\sum_{i=0}^n \frac{w_i}{x-x_i}},
  \quad\text{and set } p(x_k)=y_k.
\]`}</MathJax>
      <p>
        This “second barycentric formula” has remarkable numerical properties: it is near-optimal in terms of
        rounding error growth and requires only <em>O</em>(<MathJax inline>{"\\( n \\)"}</MathJax>) operations per evaluation
        once the weights are known. It is therefore the preferred choice in most practical interpolation software.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">7. Node Choice & Runge Phenomenon</h2>
      <p>
        The distribution of interpolation nodes is crucial for accuracy. Equidistant nodes on large intervals
        often lead to severe oscillations of the interpolant near the boundaries, known as the
        <em>Runge phenomenon</em>. A classical remedy is to use <em>Chebyshev nodes</em>, which minimize the
        maximum error by clustering points near the interval endpoints. On
        <MathJax inline>{"\\( [-1,1] \\)"}</MathJax> they are given by
      </p>
      <MathJax>{String.raw`\[
  x_k=\cos\!\left(\frac{2k+1}{2(n+1)}\pi\right),\quad k=0,\ldots,n,
\]`}</MathJax>
      <p>
        and on a general interval <MathJax inline>{"\\( [a,b] \\)"}</MathJax>, they are obtained by the affine mapping
        <MathJax inline>{"\\( x\\mapsto \\tfrac{a+b}{2} + \\tfrac{b-a}{2}x \\)"}</MathJax>.
        With this choice of nodes, the growth of interpolation error is significantly reduced, and the
        interpolant better reflects the behavior of the underlying function. As a result, Chebyshev nodes are
        the standard choice in modern polynomial interpolation practice.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">8. Polynomial Example</h2>
      <p>
        Interpolate the three points <MathJax inline>{"\\( (-1,0),\\ (0,1),\\ (1,0) \\)"}</MathJax>.
        Lagrange basis yields
      </p>
      <MathJax>{String.raw`\[
      \begin{aligned}
        p(x)= &0\cdot \frac{x(x-1)}{(-1)(-2)} + 1\cdot\frac{(x+1)(x-1)}{(0+1)(0-1)} \\
        + &0\cdot\frac{x(x+1)}{(1+1)(1-0)}
             = 1 - x^2.
             \end{aligned}
      \]`}</MathJax>
      <p>
        The Newton form leads to the same result; divided differences give nonzero entries only up to second order.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">9. Splines: Piecewise-Polynomial Interpolation</h2>
      <p>
        A <em>spline</em> is a piecewise-defined polynomial that interpolates data while enforcing continuity conditions
        at the breakpoints, also called <em>knots</em>. The main motivation for splines is to overcome the instability
        and oscillations that often appear in high-degree polynomial interpolation. Instead of fitting one global
        polynomial of degree <MathJax inline>{"\\( n \\)"}</MathJax> through all data points, splines fit low-degree
        polynomials on each subinterval and then join them together smoothly. The most widely used case is the
        <strong>cubic spline</strong>, where each subinterval <MathJax inline>{"\\( [x_i,x_{i+1}] \\)"}</MathJax> is described
        by a cubic polynomial, and at the interior knots one imposes
        <MathJax inline>{"\\( C^2 \\)"}</MathJax> continuity. This means that the spline is continuous, its first
        derivative is continuous, and its second derivative is continuous across all knots. As a result, cubic splines
        provide excellent smoothness, local control, and avoid the high-degree oscillations characteristic of
        global interpolants.
      </p>

      <h3 className="font-semibold mt-2">Cubic Spline Construction via Second Derivatives</h3>
      <p>
        A common and efficient way to construct cubic splines is through their second derivatives. Let
        <MathJax inline>{"\\( h_i = x_{i+1} - x_i \\)"}</MathJax> denote the length of the subinterval, and define the
        unknowns <MathJax inline>{"\\( m_i = S''(x_i) \\)"}</MathJax>. Imposing the interpolation conditions and the
        smoothness constraints leads to a tridiagonal linear system for the values of the second derivatives
        at the interior nodes <MathJax inline>{"\\( i = 1, \\dots, n-1 \\)"}</MathJax>:
      </p>
      <MathJax>{String.raw`\[
      \begin{aligned}
  h_{i-1} m_{i-1} &+ 2(h_{i-1}+h_i)m_i + h_i m_{i+1}\\
  &= 6\!\left(\frac{y_{i+1}-y_i}{h_i}-\frac{y_i-y_{i-1}}{h_{i-1}}\right).
  \end{aligned}
\]`}</MathJax>
      <p>
        Together with suitable boundary conditions (see below), this tridiagonal system can be solved in
        <em>O</em>(<MathJax inline>{"\\( n \\)"}</MathJax>) time. Once the values <MathJax inline>{"\\( m_i \\)"}</MathJax>
        are determined, the cubic spline on each subinterval <MathJax inline>{"\\( [x_i, x_{i+1}] \\)"}</MathJax> is given by
      </p>
      <MathJax>{String.raw`\[
      \begin{aligned}
  S(x)=&
  \frac{m_i (x_{i+1}-x)^3}{6h_i} + \frac{m_{i+1}(x-x_i)^3}{6h_i}\\
  +&\left(y_i-\frac{m_i h_i^2}{6}\right)\frac{x_{i+1}-x}{h_i}\\
  +&\left(y_{i+1}-\frac{m_{i+1} h_i^2}{6}\right)\frac{x-x_i}{h_i}.
  \end{aligned}
\]`}</MathJax>
      <p>
        This explicit formula ensures that the spline interpolates the given data points and inherits
        <MathJax inline>{"\\( C^2 \\)"}</MathJax> smoothness automatically from the construction. Each subinterval
        polynomial depends only on the values at its endpoints and their corresponding second derivatives,
        which reflects the local nature of splines.
      </p>

      <h3 className="font-semibold mt-2">Boundary Conditions</h3>
      <p>
        To uniquely determine the cubic spline, additional conditions are required at the boundary nodes.
        Several common choices exist, each suited to different applications:
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          <strong>Natural spline:</strong> impose <MathJax inline>{"\\( S''(x_0) = S''(x_n) = 0 \\)"}</MathJax>,
          corresponding to the physical condition of a beam with free ends (no bending moment).
          This choice minimizes the overall curvature of the spline and is often the default.
        </li>
        <li>
          <strong>Clamped spline:</strong> prescribe the first derivatives
          <MathJax inline>{"\\( S'(x_0) = s_0,\\; S'(x_n) = s_n \\)"}</MathJax>.
          This is appropriate when slope information is available or desired at the endpoints.
        </li>
        <li>
          <strong>Not-a-knot spline:</strong> enforce <MathJax inline>{"\\( C^3 \\)"}</MathJax> continuity at the
          first interior point <MathJax inline>{"\\( x_1 \\)"}</MathJax> and the last interior point
          <MathJax inline>{"\\( x_{n-1} \\)"}</MathJax>.
          Effectively, this removes the first and last knots, treating the first two and last two intervals
          as a single cubic. This choice is often used in software libraries to produce a “default” cubic spline.
        </li>
      </ul>
      <p>
        Each of these boundary conditions leads to a well-posed tridiagonal system, and the resulting spline
        inherits the desirable smoothness and stability properties that make cubic splines the standard tool
        for interpolation in practice.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">10. Cubic Spline Example</h2>
      <p>
        Nodes <MathJax inline>{"\\( x=[0,1,2,3]\\)"} </MathJax>, values <MathJax inline>{"\\( y=[0,1,0,1]\\)"} </MathJax>, natural spline.
        Here <MathJax inline>{"\\( h_i=1 \\)"}</MathJax>. The tridiagonal system for <MathJax inline>{"\\( m_i=S''(x_i) \\)"}</MathJax> is
      </p>
      <MathJax>{String.raw`\[
        \begin{aligned}
          &4m_1+m_2 = 6\big[(0-1)-(1-0)\big] = -12,\\
          &m_1+4m_2 = 6\big[(1-0)-(0-1)\big] = 12, \\
          &m_0=m_3=0.
        \end{aligned}
      \]`}</MathJax>
      <p>Solving: <MathJax inline>{"\\( m_1=-4,\\ m_2=4 \\)"}</MathJax>. On <MathJax inline>{"\\( [1,2] \\)"}</MathJax>:</p>
      <MathJax>{String.raw`\[
      \begin{aligned}
        S(x)= &-\frac{4}{6}(2-x)^3 + \frac{4}{6}(x-1)^3\\
              &+\left(1+\frac{4}{6}\right)(2-x) - \left(\frac{4}{6}\right)(x-1)\\
            = &-\frac{2}{3}(2-x)^3 + \frac{2}{3}(x-1)^3\\
             &+ \frac{5}{3}(2-x) - \frac{2}{3}(x-1).
            \end{aligned}
      \]`}</MathJax>
      <p>
        Similar expressions hold on other subintervals using the general formula above; the resulting curve is smooth and
        oscillation-free compared to a single high-degree polynomial through the same points.
      </p>

    </div>
  );
}
